import { ReactNode } from "react";
import {
  LocationOnOutlined,
  MoveUpOutlined,
  MultipleStopOutlined,
  ShieldOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import { TProxyPlanKey } from "@/components/newcustomize/hooks/useProxyPlanKey";
import { SubscriptionPricingType } from "@/types/SubscriptionTypes";

export const EXCLUSIVE_DISCOUNT_DESCRIPTION =
  "Exclusive discount for ISP customers. Applies to the 1 GB plan.";

/**
 * R-1595: the discount on a 1 GB Rotating Residential plan is the exclusive ISP
 * offer, so the breakdown row is relabelled "Exclusive Discount".
 */
export function isExclusiveResidentialDiscountActive(
  proxyPlanKey: string | undefined,
  bandwidth: number | undefined,
  pricing: SubscriptionPricingType | undefined
): boolean {
  return (
    proxyPlanKey === "shared_residential" &&
    bandwidth === 1 &&
    // The backend only sets this for the exclusive auto-bundle addon discount,
    // so it can't be confused with a term/count discount folded into
    // `discount_percentage` (which would false-positive for non-ISP owners).
    !!pricing?.bundle_addon_discount_percentage
  );
}

export const featureIconMap: Record<string, ReactNode> = {
  is_unlimited_ip_authorizations: (
    <LocationOnOutlined color="primary" sx={{ fontSize: 16 }} />
  ),
  is_high_concurrency: (
    <MultipleStopOutlined color="primary" sx={{ fontSize: 16 }} />
  ),
  is_high_priority_network: (
    <MoveUpOutlined color="primary" sx={{ fontSize: 16 }} />
  ),
  is_verified_proxy: <VerifiedOutlined color="primary" sx={{ fontSize: 16 }} />,
  high_quality_ips_only: (
    <ShieldOutlined color="primary" sx={{ fontSize: 16 }} />
  ),
};

export const featureTitleMap: Record<string, string> = {
  is_unlimited_ip_authorizations: "Unlimited IP Authorizations",
  is_high_concurrency: "High Concurrency",
  is_high_priority_network: "High Priority Network",
  high_quality_ips_only: "High IP Reputation",
};

export const makePlanName = (key: TProxyPlanKey) => {
  if (key.includes("verified")) return ["Verified"];
  switch (key) {
    case "shared_default":
      return ["Proxy Server", "Shared"];
    case "semidedicated_premium":
      return ["Proxy Server", "Private"];
    case "dedicated_premium":
      return ["Proxy Server", "Dedicated"];
    case "shared_isp":
      return ["Static Residential", "Shared"];
    case "semidedicated_isp":
      return ["Static Residential", "Private"];
    case "dedicated_isp":
      return ["Static Residential", "Dedicated"];
    case "shared_residential":
      return ["Residential"];
    default:
      return [];
  }
};
